<template>
  <div>
    <div class="w-full bg-white border rounded border-gray-200 shadow-sm mt-5 px-5 py-7">
      <div class="text-2xl pb-5 sm:pb-0 text-orange-organization">
        Meus Atendimentos
      </div>

      <div class="hidden sm:flex font-medium text-gray-400 my-4 relative">
        <div class="w-2/12 text-center" v-if="!isLead">
          Itens
        </div>

        <div class="flex" :class="isLead ? 'w-full' : 'w-10/12'">
          <div class="w-3/12">
            Ticket do atendimento
          </div>

          <div
            class="w-3/12 flex space-x-1 justify-center cursor-pointer hover:text-gray-500 transition duration-200"
            @click="toggleSort()"
          >
            <span>Solicitado em</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
          </div>

          <div
            class="w-3/12 flex space-x-1 justify-center cursor-pointer hover:text-gray-500 transition duration-200"
            @click.stop.prevent="showStatusMenu = !showStatusMenu"
          >
            <span>Situação</span>
            <div class="inline-flex">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
              </svg>
              <span
                v-if="selectedFilter"
                class="w-3 h-3 text-xxs -ml-2 -mt-1 text-center items-center bg-gray-600 text-white rounded-full"
              >
                1
              </span>
            </div>
          </div>
          <div
            class="absolute font-normal right-16 top-0 w-60 bg-white rounded shadow-lg border z-20 transition-all duration-200 transform"
            :class="showStatusMenu ? 'opacity-100 translate-y-6' : 'opacity-0 translate-y-2'"
          >
            <div :class="showStatusMenu ? '' : 'hidden'">
              <div
                class="p-3 hover:text-gray-500 hover:bg-gray-50 transition duration-200 cursor-pointer"
                @click="applyFilters(null)"
              >
                Todos
              </div>
              <div
                class="p-3 hover:text-gray-500 hover:bg-gray-50 transition duration-200 cursor-pointer"
                v-for="(status, index) in possibleTicketStatusTypes"
                :key="index"
                @click="applyFilters(status.type)"
              >
                {{ status.name }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!loading"
        class="h-auto overflow-y-auto sm:max-h-120 max-h-full scrollbar-thin scrollbar-thumb-rounded-full scrollbar-thumb-gray-400 scrollbar-track-gray-200"
      >
        <div
          class="w-full py-10 text-center text-gray-500 italic"
          v-if="!ticketList[0]"
        >
          <span>
            Você não possui protocolos de atendimento.
          </span>
        </div>
        <div
          class="flex min-h-28 w-full py-2 items-center bg-white rounded shadow mb-3 border-l-4"
          :class="borderColor[ticket.status.type]"
          v-for="(ticket, key) in ticketList"
          :key="key"
          v-show="!selectedFilter || ticket.status.type === selectedFilter"
        >
          <div class="inline-flex flex-col items-center justify-center w-3/12 sm:w-2/12" v-if="!isLead">
            <span class="w-full px-2 flex justify-center">
              <img
                v-if="ticket.orderItem[0] && !ticket.orderItem[0].image"
                src="@/assets/load.gif"
              >
              <img
                v-if="ticket.orderItem[0] && ticket.orderItem[0].image"
                :src="ticket.orderItem[0].image"
              >
              <div
                v-if="!ticket.orderItem[0]"
                class="h-14 w-14 rounded-full flex items-center justify-center bg-gray-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </span>
            <span class="font-medium text-xs text-gray-500" v-if="ticket.orderItem[1]">
              +{{ ticket.orderItem.length - 1 }} {{ ticket.orderItem.length - 1 === 1 ? 'item' : 'itens' }}
            </span>
          </div>

          <div class="flex flex-col sm:flex-row items-center px-3" :class="isLead ? 'w-full' : 'sm:px-0 w-9/12 sm:w-10/12'">
            <div class="flex-col w-full sm:w-3/12 items-center space-y-2">
              <span class="flex font-medium">
                #{{ ticket.cod }}
              </span>
              <span class="flex">
                {{ ticket.title | formatTitle }}
              </span>
            </div>

            <div class="w-full sm:w-3/12 text-left sm:text-center py-3 sm:py-0">
              {{ ticket.created_at | formatDate }}
            </div>

            <div class="flex items-center justify-center w-full sm:w-3/12">
              <span class="w-3 h-3">
                <span
                  v-if="ticket.status.type === awaitingCustomer.type"
                  class="flex h-3 w-3 relative"
                >
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    :class="bgColor[ticket.status.type]"
                  />
                  <span
                    class="inline-flex rounded-full w-3 h-3 text-xs justify-center text-white"
                    :class="bgColor[ticket.status.type]"
                  />
                </span>
                <span
                  v-else
                  class="flex w-3 h-3 rounded-full" :class="bgColor[ticket.status.type]"
                />
              </span>
              <span class="flex w-full sm:w-10/12 pl-2 sm:pl-0 text-center font-medium" :class="textColor[ticket.status.type]">
                {{ ticket.status.name }}
              </span>
            </div>

            <div class="flex w-full sm:w-3/12 pt-3 sm:pt-0 h-full justify-right items-center">
              <button
                class="h-10 sm:h-7 w-full sm:w-auto px-2 py-1 text-white shadow transition-opacity duration-300 border border-transparent rounded focus:outline-none focus:ring-0 bg-purple-organization hover:bg-purple-organization-light active:bg-purple-organization-dark disabled:bg-purple-organization disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="redirecting"
                @click="goToTicketDetail({ ticketId: ticket._id, status: ticket.status })"
              >
                Ver detalhes
              </button>
              <span class="flex w-4 h-4">
                <span
                  class="flex w-4 h-4 text-xs relative -top-3 -left-2 justify-center bg-orange-organization text-white rounded-full"
                  v-if="ticket.status.type === awaitingCustomer.type"
                >
                  1
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <ExternalLoading
        v-else
        text="Carregando informações"
      />

      <div
        v-if="!loading && hasMorePages"
        class="w-full flex justify-center mt-4"
      >
        <button
          class="px-4 py-2 text-white rounded shadow transition-opacity duration-300 bg-purple-organization hover:bg-purple-organization-light active:bg-purple-organization-dark disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="loadingMore"
          @click="loadMore()"
        >
          {{ loadingMore ? 'Carregando...' : 'Carregar mais atendimentos' }}
        </button>
      </div>
    </div>

    <ExternalSnackbar
      :show="snackBar.show"
      @closed="snackBar.show = false"
    >
      {{ snackBar.message }}
    </ExternalSnackbar>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { addImagesInOrderItems as addImages } from '@/utils/csExternal/images'
import { findOne as findOneCustomer, findByPathAndDocument } from '@/repositories/integrations/mobly/services/csExternal/customer'
import { findOne as findOneLead } from '@/repositories/integrations/mobly/services/csExternal/lead'
import { find as findTickets } from '@/repositories/integrations/mobly/services/csExternal/ticket'
import { AWAITING_CUSTOMER, POSSIBLE_TICKET_STATUS_TYPES } from '@/enum/enum.ticket.status.types'
import ExternalSnackbar from '@/components/commons/ExternalSnackbar'
import ExternalLoading from '@/components/commons/ExternalLoading'
import { MOBLY } from '@/utils/possible-organizations-values'

export default {
  name: 'ExternalTicketList',

  components: {
    ExternalSnackbar,
    ExternalLoading
  },

  props: {
    clientDocument: String,
    organization: String,
    type: String,
    hash: String
  },

  data () {
    return {
      loading: true,
      loadingMore: false,
      page: 1,
      totalPages: 1,
      ticketList: [],
      snackBar: {
        show: false,
        message: ''
      },
      loadingError: false,
      awaitingCustomer: AWAITING_CUSTOMER,
      possibleTicketStatusTypes: POSSIBLE_TICKET_STATUS_TYPES,
      showStatusMenu: false,
      selectedFilter: null,
      sortDesc: true,
      redirecting: false,
      borderColor: {
        canceled: 'border-gray-organization',
        done: 'border-green-organization',
        awaiting_mobly: 'border-blue-organization',
        awaiting_customer: 'border-orange-organization-pending'
      },
      textColor: {
        canceled: 'text-gray-organization',
        done: 'text-green-organization',
        awaiting_mobly: 'text-blue-organization',
        awaiting_customer: 'text-orange-organization-pending'
      },
      bgColor: {
        canceled: 'bg-gray-organization',
        done: 'bg-green-organization',
        awaiting_mobly: 'bg-blue-organization',
        awaiting_customer: 'bg-orange-organization-pending'
      },
      isLead: false
    }
  },

  computed: {
    hasMorePages () {
      return this.page < this.totalPages
    }
  },

  mounted () {
    this.init()
  },

  filters: {
    formatDate: value => (!value ? null : dayjs(value).format('DD/MM/YYYY')),
    formatTitle: value => value.length > 44 ? `${value.substr(0, 44)}...` : value
  },

  methods: {
    async init () {
      this.loading = true
      this.loadingError = false
      this.isLead = this.type === 'lead'

      try {
        if (this.organization === MOBLY && !this.isLead) {
          try {
            const statusCustomerResponse = await findByPathAndDocument(this.clientDocument, this.organization)
            const statusCustomer = statusCustomerResponse.data
            const { status } = statusCustomer

            if (!status) {
              this.$router.push('/notFound')
              return
            }

            if (status === 'blocked' || status === 'inactive') {
              this.$router.push('/blocked')
              return
            }
          } catch (e) {
            // If customer not found, check if it is a lead
            this.$router.push('/notFound')
            return
          }
        }

        // Get Client
        const clientParams = `fields=_id&hash=${this.hash}`
        if (this.isLead) {
          const clientResponse = await findOneLead(
            this.clientDocument, clientParams, this.organization
          )
          this.client = clientResponse.data
        } else {
          const clientResponse = await findOneCustomer(
            this.clientDocument, clientParams, this.organization
          )
          this.client = clientResponse.data
        }

        // Get Tickets
        await this.getTicketList()

        this.loading = false

        if (!this.isLead) {
          await this.setImages()
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          window.location.href = '/unauthorized'
        }

        this.loading = false
        this.loadingError = true

        this.snackBar.message = 'Não foi possível abrir a página requisitada'
        this.snackBar.show = true
        console.error(err)
      }
    },

    async getTicketList () {
      const paramName = this.isLead ? 'leadId' : 'customerId'
      const ticketParams = `${paramName}=${this.client._id}&page=${this.page}&sort=-created_at`
      const { data } = await findTickets(ticketParams, this.organization, this.hash)

      this.totalPages = data.pages || 1
      this.ticketList.push(...data.docs)
    },

    async loadMore () {
      this.loadingMore = true
      this.page++
      try {
        await this.getTicketList()
        if (!this.isLead) {
          await this.setImages()
        }
      } catch (err) {
        this.snackBar.message = 'Erro ao carregar mais atendimentos'
        this.snackBar.show = true
      }
      this.loadingMore = false
    },

    async setImages () {
      this.ticketList = await Promise.all(this.ticketList.map(async ticket => {
        if (!ticket.orderItem[0]) return ticket

        ticket.orderItem.sort((a, b) => {
          if (!a.invoice.charged_amount) return -1
          if (a.invoice.charged_amount > b.invoice.charged_amount) return -1
          if (b.invoice.charged_amount > a.invoice.charged_amount) return 1
          return 0
        })

        await addImages([ticket.orderItem[0]], {}, this.organization)
        return ticket
      }))
    },

    toggleSort () {
      this.sortDesc = !this.sortDesc
      this.ticketList.sort((a, b) => {
        if (!a.created_at) return this.sortDesc ? -1 : 1
        if (a.created_at > b.created_at) return this.sortDesc ? -1 : 1
        if (b.created_at > a.created_at) return this.sortDesc ? 1 : -1
        return 0
      })
    },

    applyFilters (statusType) {
      this.showStatusMenu = false
      this.selectedFilter = statusType
    },

    async goToTicketDetail ({ ticketId }) {
      this.redirecting = true
      this.$router.push(`/ticketDetail?h=${this.hash}&org=${this.organization}&t=${ticketId}`)
    }
  }
}
</script>
